import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, InputAdornment, TextField, Typography } from "@mui/material";
import { AddRounded, FilterAltRounded, SearchRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { createPurchaseOrderPath, selectedPurchaseOrderPath } from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { poUrlKey } from "../CreatePurchaseOrder/data";
import { PoGinesysSyncBadge, PoStatusBadge, PoYesNoBadge, poColors } from "../CreatePurchaseOrder/poUi";
import FilterModal from "./FilterModal/FilterModal";
import { getPoFilterColumns, listPurchaseOrdersApi, lsPoFilterValStr, poListTableColumns } from "./data";

const QUICK_FILTER_KEYS = ["po_no", "ginesys_scheme_doc_no"];

const hasActiveFilterValue = (value) => {
	if (value === undefined || value === null || value === "") return false;
	if (Array.isArray(value) && value.length === 0) return false;
	return true;
};

const formatFilterChipValue = (value) => {
	if (Array.isArray(value)) return value.join(", ");
	if (value == null) return "";
	return String(value);
};

const readStoredFilters = () => {
	try {
		if (!localStorage.getItem(lsPoFilterValStr)) return {};
		const parsed = JSON.parse(localStorage.getItem(lsPoFilterValStr));
		return typeof parsed === "object" && parsed ? parsed : {};
	} catch {
		return {};
	}
};

const AllPurchaseOrders = () => {
	const navigate = useNavigate();
	const { setLoadingOpen, snackBar, setSnackBarOpen, setSnackBar } = GlobalState();
	const [data, setData] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [filterModal, setFilterModal] = useState(false);

	const [filterColumnVal, setFilterColumnVal] = useState(() => readStoredFilters());
	const [poNoInput, setPoNoInput] = useState(() => String(readStoredFilters().po_no || ""));
	const [schemeDocInput, setSchemeDocInput] = useState(() =>
		String(readStoredFilters().ginesys_scheme_doc_no || "")
	);

	const filterColumnOptions = useMemo(
		() => getPoFilterColumns().filter((col) => !QUICK_FILTER_KEYS.includes(col.key)),
		[]
	);

	const [filterColumns, setFilterColumns] = useState(() => {
		const stored = readStoredFilters();
		return filterColumnOptions.filter(
			(row) => stored[row.key] !== undefined && hasActiveFilterValue(stored[row.key])
		);
	});

	const activeMoreFilterCount = useMemo(
		() =>
			Object.keys(filterColumnVal || {}).filter(
				(key) => !QUICK_FILTER_KEYS.includes(key) && hasActiveFilterValue(filterColumnVal[key])
			).length,
		[filterColumnVal]
	);

	const persistFilters = useCallback((next) => {
		localStorage.setItem(lsPoFilterValStr, JSON.stringify(next));
		setFilterColumnVal(next);
	}, []);

	// Debounce quick search into filterObj (Tanstack already debounces refetch ~400ms).
	useEffect(() => {
		const t = setTimeout(() => {
			setFilterColumnVal((prev) => {
				const next = { ...(prev || {}) };
				const poNo = poNoInput.trim();
				const scheme = schemeDocInput.trim();
				if (poNo) next.po_no = poNo;
				else delete next.po_no;
				if (scheme) next.ginesys_scheme_doc_no = scheme;
				else delete next.ginesys_scheme_doc_no;
				localStorage.setItem(lsPoFilterValStr, JSON.stringify(next));
				return next;
			});
		}, 350);
		return () => clearTimeout(t);
	}, [poNoInput, schemeDocInput]);

	const tableColumns = useMemo(
		() =>
			poListTableColumns.map((col) => {
				if (col.key === "status") {
					return {
						...col,
						renderCell: ({ row }) => (
							<Box width="100%" display="flex" justifyContent="center">
								<PoStatusBadge status={row.status} />
							</Box>
						),
					};
				}
				if (col.key === "order_date") {
					return {
						...col,
						valueGetter: ({ row }) => (row.order_date ? String(row.order_date).slice(0, 10) : "—"),
					};
				}
				if (col.key === "is_authorized") {
					return {
						...col,
						renderCell: ({ row }) => (
							<Box width="100%" display="flex" justifyContent="center">
								<PoYesNoBadge value={row.is_authorized} />
							</Box>
						),
					};
				}
				if (col.key === "ginesys_sync") {
					return {
						...col,
						renderCell: ({ row }) => (
							<Box width="100%" display="flex" justifyContent="center">
								<PoGinesysSyncBadge value={row.ginesys_sync} />
							</Box>
						),
					};
				}
				return col;
			}),
		[]
	);

	const fetchData = useCallback(
		async (limit, offset, filter) => {
			const isFirstLoad = data.length === 0;
			if (isFirstLoad) setLoadingOpen(true);
			try {
				const result = await listPurchaseOrdersApi({ limit, offset, filter });
				return {
					rows: result.rows || [],
					total_rows_count: result.total_rows_count ?? 0,
				};
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					severity: "error",
					message: parseErrorMessage(error),
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				return { rows: [], total_rows_count: 0 };
			} finally {
				if (isFirstLoad) setLoadingOpen(false);
			}
		},
		[data.length, setLoadingOpen, setSnackBar, setSnackBarOpen, snackBar]
	);

	const handleRowClick = ({ row }) => {
		if (!row?.id) return;
		navigate(`/${selectedPurchaseOrderPath}?${poUrlKey}=${row.id}`);
	};

	const removeActiveFilter = (key) => {
		const nextCols = filterColumns.filter((col) => col.key !== key);
		const nextVal = { ...(filterColumnVal || {}) };
		delete nextVal[key];
		setFilterColumns(nextCols);
		persistFilters(nextVal);
	};

	/** Keep quick-search fields when More Filters apply/clear. */
	const setFilterColumnValFromModal = (nextFromModal) => {
		const base = typeof nextFromModal === "function" ? nextFromModal(filterColumnVal) : nextFromModal;
		const next = { ...(base || {}) };
		const poNo = poNoInput.trim();
		const scheme = schemeDocInput.trim();
		if (poNo) next.po_no = poNo;
		else delete next.po_no;
		if (scheme) next.ginesys_scheme_doc_no = scheme;
		else delete next.ginesys_scheme_doc_no;
		persistFilters(next);
	};

	const searchFieldSx = {
		width: 180,
		flexShrink: 0,
		"& .MuiOutlinedInput-root": {
			borderRadius: "8px",
			fontSize: 13,
			bgcolor: "#fff",
			height: 36,
			"& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
		},
	};

	return (
		<Box
			sx={{
				height: "calc(100vh - 135px)",
				p: 1,
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				gap: 1,
			}}>
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<TanstackDatagrid
					columns={tableColumns}
					pageData={data}
					setPageData={setData}
					rowCount={rowCount}
					setRowCount={setRowCount}
					filterObj={filterColumnVal}
					setFilterObj={setFilterColumnVal}
					disableColumnFilter
					disableColumnSort
					disablePagination
					isLoading={false}
					emptyMessage="No purchase orders found"
					handleRowClick={handleRowClick}
					getServerSideData={fetchData}
					getRowId={(row) => row.id}>
					<Box sx={{ width: "100%", py: 0.5 }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								flexWrap: "nowrap",
								width: "100%",
								minWidth: 0,
							}}>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", flexShrink: 0, pr: 0.5 }}>
								{`${rowCount ?? 0} rows`}
							</Typography>
							<TextField
								size="small"
								value={poNoInput}
								onChange={(e) => setPoNoInput(e.target.value)}
								placeholder="PO No"
								inputProps={{ "aria-label": "PO No" }}
								sx={searchFieldSx}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchRounded sx={{ fontSize: 18, color: poColors.muted }} />
										</InputAdornment>
									),
								}}
							/>
							<TextField
								size="small"
								value={schemeDocInput}
								onChange={(e) => setSchemeDocInput(e.target.value)}
								placeholder="Ginesys Scheme Doc No"
								inputProps={{ "aria-label": "Ginesys Scheme Doc No" }}
								sx={{ ...searchFieldSx, width: 220 }}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchRounded sx={{ fontSize: 18, color: poColors.muted }} />
										</InputAdornment>
									),
								}}
							/>
							{filterColumns?.length > 0 && (
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										flexWrap: "nowrap",
										flex: "0 1 auto",
										maxWidth: "min(42vw, 420px)",
										minWidth: 0,
										overflowX: "auto",
										"&::-webkit-scrollbar": { height: 4 },
									}}>
									{filterColumns.map((row) => (
										<Chip
											key={row.key}
											variant="outlined"
											onDelete={() => removeActiveFilter(row.key)}
											label={`${row.name}: ${formatFilterChipValue(filterColumnVal[row?.key])}`}
											sx={{
												height: 36,
												fontSize: 13,
												borderRadius: "8px",
												flexShrink: 0,
												maxWidth: 260,
												bgcolor: "#fff",
												borderColor: "rgba(0,0,0,0.12)",
												"& .MuiChip-label": {
													px: 1.25,
													overflow: "hidden",
													textOverflow: "ellipsis",
												},
												"& .MuiChip-deleteIcon": { fontSize: 18 },
											}}
										/>
									))}
								</Box>
							)}
							<Button
								size="small"
								variant="outlined"
								startIcon={<FilterAltRounded sx={{ fontSize: 16 }} />}
								onClick={() => setFilterModal(true)}
								sx={{
									textTransform: "none",
									fontSize: 13,
									color: poColors.ink,
									borderColor: activeMoreFilterCount ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.18)",
									bgcolor: activeMoreFilterCount ? "rgba(0,0,0,0.04)" : "#fff",
									borderRadius: "8px",
									height: 36,
									minHeight: 36,
									px: 1.5,
									fontWeight: activeMoreFilterCount ? 600 : 500,
									flexShrink: 0,
									whiteSpace: "nowrap",
								}}>
								More Filters{activeMoreFilterCount > 0 ? ` (${activeMoreFilterCount})` : ""}
							</Button>
							<Box sx={{ flex: 1, minWidth: 8 }} />
							<Button
								variant="contained"
								size="small"
								startIcon={<AddRounded />}
								onClick={() => navigate(`/${createPurchaseOrderPath}`)}
								sx={{
									textTransform: "capitalize",
									flexShrink: 0,
									whiteSpace: "nowrap",
									height: 36,
									borderRadius: "8px",
								}}>
								Create
							</Button>
						</Box>
					</Box>
				</TanstackDatagrid>
			</Box>

			{filterModal && (
				<FilterModal
					modal={filterModal}
					handleCloseModal={() => setFilterModal(false)}
					filterColumns={filterColumns}
					setFilterColumns={setFilterColumns}
					filterColumnVal={filterColumnVal}
					setFilterColumnVal={setFilterColumnValFromModal}
					localStorageStr={lsPoFilterValStr}
					allColumns={filterColumnOptions}
					manageLocalStorage={false}
				/>
			)}
		</Box>
	);
};

export default AllPurchaseOrders;
